import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./PlanejamentoMensalPage.module.css";
import { wf1Api } from "../wf1/api";
import api from "../../api/axios";
import {
  aprovarPlanejamentoMensalCoordenador,
  getAjusteMensalPlano,
  getPlanejamentoMensalPlano,
  salvarPlanejamentoMensalPlano,
} from "./api";

const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const currentMonth = () => ymd(new Date()).slice(0, 7);
const monthKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const addDays = (d, days) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};
const startOfWeekSunday = (d) => {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setDate(copy.getDate() - copy.getDay());
  return copy;
};
const dayMonth = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;

function monthLabel(value) {
  if (!value) return "";
  const [y, m] = value.split("-");
  if (!y || !m) return value;
  try {
    const dt = new Date(Number(y), Number(m) - 1, 1);
    const label = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(dt);
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return value;
  }
}

function weeksForMonth(value) {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return [];
  const [y, m] = value.split("-").map(Number);
  const targetMonth = `${y}-${pad2(m)}`;
  const monthStart = new Date(y, m - 1, 1);
  const monthEnd = new Date(y, m, 0);
  const firstWeekStart = startOfWeekSunday(monthStart);
  const lastWeekStart = startOfWeekSunday(monthEnd);

  const weeks = [];
  for (let weekStart = new Date(firstWeekStart); weekStart <= lastWeekStart; weekStart = addDays(weekStart, 7)) {
    const weekEnd = addDays(weekStart, 6);
    const daysPerMonth = new Map();

    for (let i = 0; i < 7; i += 1) {
      const day = addDays(weekStart, i);
      const key = monthKey(day);
      daysPerMonth.set(key, (daysPerMonth.get(key) || 0) + 1);
    }

    const ownerMonth = Array.from(daysPerMonth.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (ownerMonth !== targetMonth) continue;

    weeks.push({
      id: weeks.length + 1,
      start: ymd(weekStart),
      end: ymd(weekEnd),
      label: `${dayMonth(weekStart)}-${dayMonth(weekEnd)}`,
    });
  }

  return weeks;
}

function parseTimeToMinutes(raw) {
  if (raw === null || raw === undefined) return 0;
  const str = String(raw).trim();
  if (!str) return 0;
  const normalized = str.replace(",", ".");
  if (normalized.includes(":")) {
    const [h, m] = normalized.split(":");
    const hours = Number(h) || 0;
    const mins = Number(m) || 0;
    return hours * 60 + mins;
  }
  const num = Number(normalized);
  if (Number.isFinite(num)) return Math.round(num * 60);
  return 0;
}

function formatMinutesHHMM(totalMinutes) {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

function normalizeTimeTyping(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function normalizeTimeBlur(raw) {
  const str = String(raw || "").trim();
  if (!str) return "";
  return formatMinutesHHMM(parseTimeToMinutes(str));
}

function normalizeQtyTyping(raw) {
  return String(raw || "").replace(/\D/g, "").slice(0, 8);
}

function hasFieldValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function normalizeGroup(value) {
  const v = String(value || "").trim();
  return v || "Sem grupo";
}

function normalizeCoordenadores(rows = []) {
  const out = [];
  const seen = new Set();

  for (const c of rows || []) {
    if (c == null) continue;

    const nomeRaw =
      typeof c === "string"
        ? c
        : (c.coordenador || c.coordenador_nome || c.nome || "");

    const nome = String(nomeRaw || "").trim();
    if (!nome) continue;

    const key = nome.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const nos = Array.isArray(c?.nos)
      ? Array.from(new Set(c.nos.map((n) => String(n || "").trim()).filter(Boolean)))
      : [];

    out.push({ nome, nos });
  }

  return out.sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
}

function planKey(coord, atividade, grupo, semana, field) {
  return `${coord}__${grupo}__${atividade}__${semana}__${field}`;
}

function cellKey(coord, atividade, grupo, semana) {
  return JSON.stringify([coord, atividade, grupo, Number(semana)]);
}

function coordApprovalKey(gerencia, coordenador) {
  return `${String(gerencia || "").trim()}__${String(coordenador || "").trim()}`;
}

export default function PlanejamentoMensalPage({ onBack }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [month, setMonth] = useState(currentMonth);
  const [coordQuery, setCoordQuery] = useState("");

  const [loadingBase, setLoadingBase] = useState(false);
  const [coordenadores, setCoordenadores] = useState([]);
  const [coordenadoresPorGerencia, setCoordenadoresPorGerencia] = useState({});
  const [loadingCoordenadoresTree, setLoadingCoordenadoresTree] = useState(false);
  const [atividades, setAtividades] = useState([]);
  const [gerencias, setGerencias] = useState([]);

  const [planMap, setPlanMap] = useState({});
  const [erro] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [aprovacoesMap, setAprovacoesMap] = useState({});
  const [aprovandoCoord, setAprovandoCoord] = useState("");

  const dirtyCellsRef = useRef(new Set());
  const saveTimerRef = useRef(null);
  const hydratingRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const fromQuery = String(params.get("month") || "").trim();
    if (/^\d{4}-\d{2}$/.test(fromQuery) && fromQuery !== month) {
      setMonth(fromQuery);
    }
  }, [location.search, month]);

  useEffect(() => {
    let alive = true;
    setLoadingBase(true);

    Promise.all([
      wf1Api.getGerencias(),
      wf1Api.getAtividades(),
      api.get("/atividades", { params: { limit: 1000 } }).catch(() => null),
    ])
      .then(([g, a, fullAtividadesRes]) => {
        if (!alive) return;

        const atividadesNomes = a.data?.data || [];
        const fullAtividades = fullAtividadesRes?.data?.data || [];
        const hasGroupInNomes = atividadesNomes.some((it) => String(it?.grupo || "").trim());

        let atividadesComGrupo = atividadesNomes;

        if (!hasGroupInNomes && fullAtividades.length) {
          const byAtividadeUnidade = new Map();
          const byAtividade = new Map();

          for (const row of fullAtividades) {
            const atividade = String(row?.atividade || row?.nome || "").trim();
            if (!atividade) continue;

            const unidade = String(row?.unidade || row?.unidade_de_medida || "").trim();
            const grupo = String(row?.grupo || "").trim();
            const key = `${atividade}__${unidade}`;

            if (!byAtividadeUnidade.has(key)) {
              byAtividadeUnidade.set(key, { grupo });
            }
            if (!byAtividade.has(atividade)) {
              byAtividade.set(atividade, { grupo });
            }
          }

          atividadesComGrupo = atividadesNomes.map((row) => {
            const atividade = String(row?.atividade || row?.nome || "").trim();
            const unidade = String(row?.unidade || row?.unidade_de_medida || "").trim();
            const key = `${atividade}__${unidade}`;

            const grupo =
              String(row?.grupo || "").trim() ||
              String(byAtividadeUnidade.get(key)?.grupo || "").trim() ||
              String(byAtividade.get(atividade)?.grupo || "").trim();

            return { ...row, grupo };
          });
        }

        setGerencias(g.data?.data || []);
        setAtividades(atividadesComGrupo);
      })
      .finally(() => {
        if (!alive) return;
        setLoadingBase(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const gerenciasLabels = useMemo(() => {
    return (gerencias || [])
      .map((g) => String(g?.gerencia || g?.nome || g || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [gerencias]);

  useEffect(() => {
    if (!gerenciasLabels.length) {
      setCoordenadoresPorGerencia({});
      return;
    }

    let alive = true;
    setLoadingCoordenadoresTree(true);

    Promise.all(
      gerenciasLabels.map(async (g) => {
        try {
          const r = await wf1Api.getCoordenadores(g, { detalhado: true });
          return [g, normalizeCoordenadores(r.data?.data || [])];
        } catch {
          return [g, []];
        }
      })
    )
      .then((pairs) => {
        if (!alive) return;
        setCoordenadoresPorGerencia(Object.fromEntries(pairs));
      })
      .finally(() => {
        if (!alive) return;
        setLoadingCoordenadoresTree(false);
      });

    return () => {
      alive = false;
    };
  }, [gerenciasLabels]);

  useEffect(() => {
    const all = Object.values(coordenadoresPorGerencia || {}).flat();
    setCoordenadores(normalizeCoordenadores(all));
  }, [coordenadoresPorGerencia]);

  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    navigate("/");
  };

  const goToAjusteMensal = () => {
    const qs = new URLSearchParams({ month }).toString();
    navigate(`/programacoes/planejamento-mensal/ajuste?${qs}`);
  };

  const atividadesList = useMemo(() => {
    return (atividades || [])
      .map((a, idx) => {
        const atividade = a.atividade || a.nome || a;
        const unidade = a.unidade || a.unidade_de_medida || "";
        const grupo = normalizeGroup(a.grupo || a.grupo_atividade || "");
        return {
          atividade,
          unidade,
          grupo,
          itemKey: `${atividade}__${unidade || "-"}__${grupo}__${idx}`,
        };
      })
      .filter((a) => a.atividade)
      .sort((a, b) => String(a.atividade).localeCompare(String(b.atividade), "pt-BR"));
  }, [atividades]);

  const atividadesPorGrupo = useMemo(() => {
    const map = new Map();
    for (const atv of atividadesList) {
      const grupo = normalizeGroup(atv.grupo);
      if (!map.has(grupo)) map.set(grupo, []);
      map.get(grupo).push(atv);
    }

    return Array.from(map.entries())
      .map(([grupo, itens]) => ({
        grupo,
        itens: itens.sort((a, b) => String(a.atividade).localeCompare(String(b.atividade), "pt-BR")),
      }))
      .sort((a, b) => String(a.grupo).localeCompare(String(b.grupo), "pt-BR"));
  }, [atividadesList]);

  const coordenadorList = useMemo(() => normalizeCoordenadores(coordenadores || []), [coordenadores]);

  const weeks = useMemo(() => weeksForMonth(month), [month]);

  const coordenadorListFiltrado = useMemo(() => {
    const q = coordQuery.trim().toLowerCase();
    if (!q) return coordenadorList;
    return coordenadorList.filter((c) => {
      const nome = String(c.nome || "").toLowerCase();
      const nos = (c.nos || []).join(" ").toLowerCase();
      return nome.includes(q) || nos.includes(q);
    });
  }, [coordenadorList, coordQuery]);

  const resumo = useMemo(() => {
    const coordTotal = coordenadorList.length;
    const coordVisiveis = coordenadorListFiltrado.length;
    const atividadesTotal = atividadesList.length;
    const gruposAtividade = atividadesPorGrupo.length;
    const semanasTotal = weeks.length;
    const linhas = coordVisiveis * atividadesTotal;
    const campos = linhas * semanasTotal * 2;

    return {
      coordTotal,
      coordVisiveis,
      atividadesTotal,
      gruposAtividade,
      semanasTotal,
      linhas,
      campos,
    };
  }, [
    coordenadorList.length,
    coordenadorListFiltrado.length,
    atividadesList.length,
    atividadesPorGrupo.length,
    weeks.length,
  ]);

  const atividadeByItemKey = useMemo(() => {
    const map = new Map();
    for (const atv of atividadesList) {
      map.set(atv.itemKey, atv);
    }
    return map;
  }, [atividadesList]);

  const atividadeLookup = useMemo(() => {
    const map = new Map();
    for (const atv of atividadesList) {
      const atividade = String(atv.atividade || "").trim();
      const unidade = String(atv.unidade || "").trim();
      const grupo = normalizeGroup(atv.grupo);
      if (!atividade) continue;

      if (!map.has(`${atividade}__${unidade}__${grupo}`)) map.set(`${atividade}__${unidade}__${grupo}`, atv.itemKey);
      if (!map.has(`${atividade}__${unidade}`)) map.set(`${atividade}__${unidade}`, atv.itemKey);
      if (!map.has(atividade)) map.set(atividade, atv.itemKey);
    }
    return map;
  }, [atividadesList]);

  const coordenadorGerenciaMap = useMemo(() => {
    const map = new Map();
    for (const [gerencia, coords] of Object.entries(coordenadoresPorGerencia || {})) {
      for (const coord of normalizeCoordenadores(coords || [])) {
        if (!map.has(coord.nome)) map.set(coord.nome, gerencia);
      }
    }
    return map;
  }, [coordenadoresPorGerencia]);

  useEffect(() => {
    if (!month) return;

    let alive = true;
    getAjusteMensalPlano({ month })
      .then((out) => {
        if (!alive) return;
        const rows = Array.isArray(out?.rows) ? out.rows : [];
        const next = {};
        for (const row of rows) {
          const key = coordApprovalKey(row?.gerencia, row?.coordenador);
          if (!String(row?.coordenador || "").trim()) continue;
          next[key] = {
            gerencia: row?.gerencia || "",
            coordenador: row?.coordenador || "",
            aprovado_em: row?.aprovado_em || null,
            aprovado_por: row?.aprovado_por || null,
          };
        }
        setAprovacoesMap(next);
      })
      .catch(() => {
        if (!alive) return;
        setAprovacoesMap({});
      });

    return () => {
      alive = false;
    };
  }, [month]);

  useEffect(() => {
    if (!month || !atividadeLookup.size) return;

    let alive = true;
    hydratingRef.current = true;
    setSaveStatus("loading");

    getPlanejamentoMensalPlano({ month })
      .then((out) => {
        if (!alive) return;

        const rows = Array.isArray(out?.rows) ? out.rows : [];
        const nextMap = {};

        for (const row of rows) {
          const semana = Number(row?.semana);
          if (!Number.isFinite(semana)) continue;

          const atividade = String(row?.atividade || "").trim();
          if (!atividade) continue;
          const unidade = String(row?.unidade || "").trim();
          const grupo = normalizeGroup(row?.grupo || "");
          const coordenador = String(row?.coordenador || "").trim();
          if (!coordenador) continue;

          const itemKey =
            atividadeLookup.get(`${atividade}__${unidade}__${grupo}`) ||
            atividadeLookup.get(`${atividade}__${unidade}`) ||
            atividadeLookup.get(atividade);
          if (!itemKey) continue;

          const qtdRaw = row?.quantidade;
          const tempoRaw = normalizeTimeBlur(
            row?.tempo != null && String(row.tempo).trim() !== ""
              ? row.tempo
              : row?.tempo_min
          );

          const keyQtd = planKey(coordenador, itemKey, grupo, semana, "qtd");
          const keyTempo = planKey(coordenador, itemKey, grupo, semana, "tempo");

          if (qtdRaw != null && qtdRaw !== "") nextMap[keyQtd] = String(qtdRaw);
          if (tempoRaw != null && String(tempoRaw).trim() !== "") nextMap[keyTempo] = String(tempoRaw);
        }

        dirtyCellsRef.current.clear();
        setPlanMap(nextMap);
        setSaveStatus("idle");
      })
      .catch(() => {
        if (!alive) return;
        setSaveStatus("error");
      })
      .finally(() => {
        if (!alive) return;
        hydratingRef.current = false;
      });

    return () => {
      alive = false;
      hydratingRef.current = false;
    };
  }, [month, atividadeLookup]);

  useEffect(() => {
    if (hydratingRef.current) return;
    if (!dirtyCellsRef.current.size) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      const dirty = Array.from(dirtyCellsRef.current);
      dirtyCellsRef.current.clear();

      const entries = [];
      for (const encoded of dirty) {
        let parsed = null;
        try {
          parsed = JSON.parse(encoded);
        } catch {
          parsed = null;
        }
        if (!Array.isArray(parsed) || parsed.length < 4) continue;

        const [coordenador, atividadeKey, grupo, semanaRaw] = parsed;
        const semana = Number(semanaRaw);
        if (!Number.isFinite(semana)) continue;

        const atividadeMeta = atividadeByItemKey.get(atividadeKey);
        if (!atividadeMeta) continue;

        const gerencia = coordenadorGerenciaMap.get(coordenador);
        if (!gerencia) continue;
        if (aprovacoesMap[coordApprovalKey(gerencia, coordenador)]) continue;

        const keyQtd = planKey(coordenador, atividadeKey, grupo, semana, "qtd");
        const keyTempo = planKey(coordenador, atividadeKey, grupo, semana, "tempo");
        const quantidade = planMap[keyQtd] ?? "";
        const tempo = planMap[keyTempo] ?? "";

        entries.push({
          gerencia,
          coordenador,
          atividade: atividadeMeta.atividade,
          grupo: atividadeMeta.grupo || grupo,
          unidade: atividadeMeta.unidade || null,
          semana,
          quantidade,
          tempo,
        });
      }

      if (!entries.length) {
        setSaveStatus("idle");
        return;
      }

      try {
        setSaveStatus("saving");
        await salvarPlanejamentoMensalPlano({ month, entries });
        setSaveStatus("saved");
        setTimeout(() => {
          setSaveStatus((prev) => (prev === "saved" ? "idle" : prev));
        }, 1200);
      } catch {
        for (const encoded of dirty) dirtyCellsRef.current.add(encoded);
        setSaveStatus("error");
      }
    }, 900);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [planMap, month, atividadeByItemKey, coordenadorGerenciaMap, aprovacoesMap]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const saveStatusLabel = useMemo(() => {
    if (saveStatus === "loading") return "Carregando planejamento salvo";
    if (saveStatus === "dirty") return "Alteracoes pendentes";
    if (saveStatus === "saving") return "Autosave em andamento";
    if (saveStatus === "saved") return "Autosave salvo";
    if (saveStatus === "error") return "Falha no autosave";
    return "Autosave pronto";
  }, [saveStatus]);

  const isCoordApproved = (coordenadorNome) => {
    const gerencia = coordenadorGerenciaMap.get(coordenadorNome) || "";
    return !!aprovacoesMap[coordApprovalKey(gerencia, coordenadorNome)];
  };

  const handleAprovarCoordenador = async (coordenadorNome) => {
    const gerencia = coordenadorGerenciaMap.get(coordenadorNome) || "";
    if (!month || !gerencia || !coordenadorNome) return;

    setAprovandoCoord(coordenadorNome);
    try {
      const out = await aprovarPlanejamentoMensalCoordenador({
        month,
        gerencia,
        coordenador: coordenadorNome,
      });

      if (!out?.ok) {
        setSaveStatus("error");
        return;
      }

      setAprovacoesMap((prev) => ({
        ...prev,
        [coordApprovalKey(gerencia, coordenadorNome)]: {
          gerencia,
          coordenador: coordenadorNome,
          aprovado_em: out?.aprovado_em || new Date().toISOString(),
          aprovado_por: out?.aprovado_por || "",
        },
      }));

      dirtyCellsRef.current = new Set(
        Array.from(dirtyCellsRef.current).filter((encoded) => {
          try {
            const parsed = JSON.parse(encoded);
            return parsed?.[0] !== coordenadorNome;
          } catch {
            return true;
          }
        })
      );

      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus((prev) => (prev === "saved" ? "idle" : prev));
      }, 1200);
    } catch {
      setSaveStatus("error");
    } finally {
      setAprovandoCoord("");
    }
  };

  const updateValue = (coord, atividade, grupo, semana, field, value) => {
    if (isCoordApproved(coord)) return;
    const key = planKey(coord, atividade, grupo, semana, field);
    dirtyCellsRef.current.add(cellKey(coord, atividade, grupo, semana));
    if (saveStatus !== "saving") setSaveStatus("dirty");
    setPlanMap((prev) => ({ ...prev, [key]: value }));
  };

  const getQty = (coord, atividade, grupo, semana) => {
    const key = planKey(coord, atividade, grupo, semana, "qtd");
    const val = Number(planMap[key]);
    return Number.isFinite(val) ? val : 0;
  };

  const getTempoMin = (coord, atividade, grupo, semana) => {
    const key = planKey(coord, atividade, grupo, semana, "tempo");
    return parseTimeToMinutes(planMap[key]);
  };

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <button className={styles.menuBtn} type="button" onClick={goBack}>Menu principal</button>

          <div className={styles.sideBlock}>
            <div className={styles.sideTitle}>Planejamento mensal</div>
            <div className={styles.sideSubtitle}>{monthLabel(month)}</div>
            <div className={styles.sideHint}>Preenchimento por coordenador e por grupo de atividades.</div>
          </div>

          <div className={styles.sideBlock}>
            <label className={styles.sideField}>
              <span>Mês</span>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>

            <label className={styles.sideField}>
              <span>Buscar coordenador</span>
              <input
                type="text"
                value={coordQuery}
                onChange={(e) => setCoordQuery(e.target.value)}
                placeholder="Nome ou NO"
              />
            </label>

            <div className={styles.gerTreeHead}>Gerências e coordenadores</div>
            {loadingCoordenadoresTree ? (
              <div className={styles.sideMiniInfo}>Carregando coordenadores...</div>
            ) : null}

            <div className={styles.gerenciaTree}>
              {gerenciasLabels.map((g) => {
                const coords = coordenadoresPorGerencia[g] || [];
                return (
                  <section key={g} className={styles.gerenciaItem}>
                    <div className={styles.gerenciaItemHead}>
                      <button
                        type="button"
                        onClick={() => {
                          setCoordQuery("");
                        }}
                      >
                        {g}
                      </button>
                      <span>{coords.length}</span>
                    </div>

                    <div className={styles.coordPills}>
                      {coords.length ? (
                        coords.map((c) => (
                          <button
                            key={`${g}__${c.nome}`}
                            type="button"
                            className={styles.coordPill}
                            onClick={() => {
                              setCoordQuery(c.nome);
                            }}
                            title={`Filtrar por ${c.nome}`}
                          >
                            {c.nome}
                          </button>
                        ))
                      ) : (
                        <span className={styles.coordEmpty}>Sem coordenadores</span>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          <div className={styles.sideStats}>
            <div className={styles.sideStat}>
              <span>Coordenadores</span>
              <strong>{resumo.coordVisiveis}/{resumo.coordTotal}</strong>
            </div>
            <div className={styles.sideStat}>
              <span>Grupos de atividades</span>
              <strong>{resumo.gruposAtividade}</strong>
            </div>
            <div className={styles.sideStat}>
              <span>Atividades</span>
              <strong>{resumo.atividadesTotal}</strong>
            </div>
            <div className={styles.sideStat}>
              <span>Campos para preencher</span>
              <strong>{resumo.campos}</strong>
            </div>
          </div>
        </aside>

        <main className={styles.main}>
          <header className={styles.mainHead}>
            <h2>Preenchimento operacional</h2>
            <p>
              Defina quantidade e hora por semana. As atividades estão separadas por grupo para
              facilitar a distribuição e a revisão.
            </p>
            <div className={styles.mainBadges}>
              <span>{resumo.coordVisiveis} coordenador(es)</span>
              <span>{resumo.linhas} linhas de atividade</span>
              <span>{resumo.semanasTotal} semana(s)</span>
              <span>{saveStatusLabel}</span>
              <button type="button" className={styles.mainBadgeButton} onClick={goToAjusteMensal}>
                Ir para ajuste mensal
              </button>
            </div>
          </header>

          <section className={styles.panel}>
            {erro ? <div className={styles.empty}>{erro}</div> : null}
            {loadingBase ? <div className={styles.loading}>Carregando base...</div> : null}

            {!loadingCoordenadoresTree && !coordenadorList.length ? (
              <div className={styles.empty}>Nenhum coordenador encontrado.</div>
            ) : null}

            {!!coordenadorList.length && !coordenadorListFiltrado.length ? (
              <div className={styles.empty}>Nenhum coordenador corresponde ao filtro informado.</div>
            ) : null}

            <div className={styles.coordinatorGrid}>
              {coordenadorListFiltrado.map((coord) => {
                const coordGerencia = coordenadorGerenciaMap.get(coord.nome) || "";
                const coordLocked = !!aprovacoesMap[coordApprovalKey(coordGerencia, coord.nome)];
                return (
                <section
                  key={coord.nome}
                  className={`${styles.coordCard} ${coordLocked ? styles.coordCardLocked : ""}`}
                >
                  <div className={styles.coordSummary}>
                    <div className={styles.coordSummaryLeft}>
                      <span>{coord.nome}</span>
                      <span className={styles.coordHint}>
                        {coord.nos?.length ? `NO: ${coord.nos.join(", ")}` : "Sem NO cadastrado"}
                      </span>
                    </div>

                    <div className={styles.coordSummaryRight}>
                      {coordLocked ? <span className={styles.coordApprovedBadge}>Aprovado</span> : null}
                      <button
                        type="button"
                        className={styles.coordApproveBtn}
                        disabled={coordLocked || !coordGerencia || aprovandoCoord === coord.nome}
                        onClick={() => handleAprovarCoordenador(coord.nome)}
                      >
                        {coordLocked
                          ? "Aprovado"
                          : aprovandoCoord === coord.nome
                            ? "Aprovando..."
                            : "Aprovar coordenador"}
                      </button>
                    </div>
                  </div>

                  <div className={styles.groupsStack}>
                    {(() => {
                      const coordWeekTotals = weeks.map(() => ({ qtd: 0, tempoMin: 0 }));
                      let coordQtd = 0;
                      let coordTempo = 0;

                      const blocos = atividadesPorGrupo.map((grupoData) => {
                        const groupWeekTotals = weeks.map(() => ({ qtd: 0, tempoMin: 0 }));
                        let grupoQtd = 0;
                        let grupoTempo = 0;

                        const rows = grupoData.itens.map((a) => {
                          let rowQtd = 0;
                          let rowTempo = 0;

                          weeks.forEach((w, weekIndex) => {
                            const q = getQty(coord.nome, a.itemKey, grupoData.grupo, w.id);
                            const t = getTempoMin(coord.nome, a.itemKey, grupoData.grupo, w.id);
                            const tempoTotalSemana = q * t;

                            rowQtd += q;
                            rowTempo += tempoTotalSemana;

                            groupWeekTotals[weekIndex].qtd += q;
                            groupWeekTotals[weekIndex].tempoMin += tempoTotalSemana;

                            coordWeekTotals[weekIndex].qtd += q;
                            coordWeekTotals[weekIndex].tempoMin += tempoTotalSemana;
                          });

                          grupoQtd += rowQtd;
                          grupoTempo += rowTempo;
                          coordQtd += rowQtd;
                          coordTempo += rowTempo;

                          return {
                            atividade: a,
                            rowQtd,
                            rowTempo,
                          };
                        });

                        return {
                          grupo: grupoData.grupo,
                          rows,
                          groupWeekTotals,
                          grupoQtd,
                          grupoTempo,
                        };
                      });

                      if (!blocos.length) {
                        return <div className={styles.empty}>Sem atividades cadastradas para agrupamento.</div>;
                      }

                      return (
                        <>
                          {blocos.map((bloco) => (
                            <section key={`${coord.nome}__${bloco.grupo}`} className={styles.groupBlock}>
                              <header className={styles.groupHead}>
                                <h4>{bloco.grupo}</h4>
                                <span>{bloco.rows.length} atividade(s)</span>
                              </header>

                              <div className={styles.matrixWrap}>
                                <table className={styles.matrix}>
                                  <thead>
                                    <tr>
                                      <th className={styles.matrixSticky}>Atividade</th>
                                      <th className={`${styles.matrixSticky} ${styles.matrixSticky2}`}>Unidade</th>
                                      {weeks.map((w) => (
                                        <th key={w.id} className={styles.weekCol}>
                                          Sem {w.id}
                                          <br />
                                          <span>{w.label}</span>
                                          <div className={styles.weekSub}>Qtd. / Hora (hh:mm)</div>
                                        </th>
                                      ))}
                                      <th>Total mês</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {bloco.rows.map((r) => {
                                      const weekCells = weeks.map((w) => {
                                        const keyQtd = planKey(coord.nome, r.atividade.itemKey, bloco.grupo, w.id, "qtd");
                                        const keyTempo = planKey(coord.nome, r.atividade.itemKey, bloco.grupo, w.id, "tempo");

                                        const valQtd = planMap[keyQtd] ?? "";
                                        const valTempo = planMap[keyTempo] ?? "";
                                        const isFilled = hasFieldValue(valQtd) && hasFieldValue(valTempo);

                                        return { w, valQtd, valTempo, isFilled };
                                      });

                                      const rowFilled = weekCells.length > 0 && weekCells.every((c) => c.isFilled);

                                      return (
                                        <tr
                                          key={`${coord.nome}__${bloco.grupo}__${r.atividade.itemKey}`}
                                          className={rowFilled ? styles.rowDone : undefined}
                                        >
                                          <td className={styles.matrixSticky}>{r.atividade.atividade}</td>
                                          <td className={`${styles.matrixSticky} ${styles.matrixSticky2}`}>
                                            {r.atividade.unidade || "-"}
                                          </td>

                                          {weekCells.map(({ w, valQtd, valTempo, isFilled }) => (
                                            <td
                                              key={w.id}
                                              className={`${styles.weekCol} ${isFilled ? styles.weekDone : ""}`}
                                            >
                                              <div className={styles.cellInputs}>
                                                <input
                                                  className={`${styles.matrixInput} ${styles.matrixInputQty}`}
                                                  type="text"
                                                  inputMode="numeric"
                                                  pattern="\d*"
                                                  value={valQtd}
                                                  placeholder="Quantidade"
                                                  disabled={coordLocked}
                                                  onChange={(e) =>
                                                    updateValue(
                                                      coord.nome,
                                                      r.atividade.itemKey,
                                                      bloco.grupo,
                                                      w.id,
                                                      "qtd",
                                                      normalizeQtyTyping(e.target.value)
                                                    )
                                                  }
                                                />
                                                <input
                                                  className={`${styles.matrixInput} ${styles.matrixInputHora}`}
                                                  type="text"
                                                  inputMode="numeric"
                                                  maxLength={5}
                                                  value={valTempo}
                                                  placeholder="hh:mm"
                                                  disabled={coordLocked}
                                                  onChange={(e) =>
                                                    updateValue(
                                                      coord.nome,
                                                      r.atividade.itemKey,
                                                      bloco.grupo,
                                                      w.id,
                                                      "tempo",
                                                      normalizeTimeTyping(e.target.value)
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    updateValue(
                                                      coord.nome,
                                                      r.atividade.itemKey,
                                                      bloco.grupo,
                                                      w.id,
                                                      "tempo",
                                                      normalizeTimeBlur(valTempo)
                                                    )
                                                  }
                                                />
                                              </div>
                                            </td>
                                          ))}

                                          <td className={styles.totalCell}>
                                            <div className={styles.cellStack}>
                                              <span className={styles.totalValue}>Quantidade: {r.rowQtd}</span>
                                              <span className={styles.totalValue}>Tempo: {formatMinutesHHMM(r.rowTempo)}</span>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>

                                  <tfoot>
                                    <tr>
                                      <td className={styles.totalRowLabel} colSpan={2}>Total do grupo</td>
                                      {bloco.groupWeekTotals.map((t, i) => (
                                        <td key={i}>
                                          <div className={styles.cellStack}>
                                            <span className={styles.totalValue}>Quantidade: {t.qtd}</span>
                                            <span className={styles.totalValue}>Tempo: {formatMinutesHHMM(t.tempoMin)}</span>
                                          </div>
                                        </td>
                                      ))}
                                      <td className={styles.totalCell}>
                                        <div className={styles.cellStack}>
                                          <span className={styles.totalValue}>Quantidade: {bloco.grupoQtd}</span>
                                          <span className={styles.totalValue}>Tempo: {formatMinutesHHMM(bloco.grupoTempo)}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </section>
                          ))}

                          <div className={styles.coordTotals}>
                            <div className={styles.coordTotalsMain}>
                              <strong>Total do coordenador</strong>
                              <span>Quantidade: {coordQtd}</span>
                              <span>Tempo: {formatMinutesHHMM(coordTempo)}</span>
                            </div>
                            <div className={styles.coordWeekTotals}>
                              {weeks.map((w, weekIndex) => (
                                <span key={w.id}>
                                  Sem {w.id}: {coordWeekTotals[weekIndex].qtd} / {formatMinutesHHMM(coordWeekTotals[weekIndex].tempoMin)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </section>
              );
              })}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
